import { Loader } from '@ksyos/design-system';
import { SelectSleepRecorder } from '@frontend/components/elements/SelectSleepRecorder';
import { ReloadEpisode } from '@frontend/components/pages/episodes/reloadEpisode';
import { getSelectedLanguage } from '@frontend/translate';
import { osasExaminationCanCreateOsasq } from '@frontend/utils/osasExaminationCanCreateOsasq';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { fetchJson } from '../../../../../store/fetch';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { OsasqConfirmationModal } from '../../OSASQ/v1/OsasqConfirmationModal';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';

interface IInnerOsasvoV1RequestAddEditState {
    buttonDisabled?: boolean;
    canReferPatientWithoutSmartphone: boolean;
    isLoading: boolean;
}

export function OsasvoV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    return <InnerOsasvoV1RequestAddEdit {...props} />;
}

class InnerOsasvoV1RequestAddEdit extends Component<
    ICustomControlRequestAddEditProps,
    IInnerOsasvoV1RequestAddEditState
> {
    public async componentDidMount() {
        this.setState({ isLoading: true });

        await this.getOsasvServiceOrganizationSettings();
        await this.getShouldCreateOsasq();

        this.setState({ isLoading: false });
    }

    constructor(props: ICustomControlRequestAddEditProps, context?: any) {
        super(props, context);

        this.state = {
            isLoading: true,
            canReferPatientWithoutSmartphone: false,
        };
    }

    public render() {
        return (
            <>
                <NewOrEditRequestProcessContainer
                    isExaminationRequest
                    isFirstItem={this.props.isFirstItem}
                    itemState={this.props.itemState}
                    process={this.props.process}
                    service={this.props.service}
                    title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
                >
                    {this.state.isLoading ? (
                        <Loader
                            className="mt-3 mx-auto"
                            size="large"
                        />
                    ) : (
                        this.addEditForm()
                    )}
                </NewOrEditRequestProcessContainer>

                {this.props.request.customControlData.shouldCreateOsasq && (
                    <OsasqConfirmationModal
                        patientId={this.props.request.patientId}
                        serviceId={this.props.request.serviceId}
                    />
                )}
            </>
        );
    }

    private addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.request}
                />

                <SelectSleepRecorder
                    canReferPatientWithoutSmartphone={this.state.canReferPatientWithoutSmartphone}
                    errors={this.props.errors}
                    hasErrors={
                        this.props.errors.has('customControlData.typeSlaaprecorder') ||
                        this.props.errors.has('customControlData.slaaprecorderVerzenden')
                    }
                    onChange={async ({ distributionMethod, sleepRecorderType, patientHasSmartphone }) => {
                        await this.props.handleChangeCustomControl({
                            typeSlaaprecorder: sleepRecorderType,
                            slaaprecorderVerzenden: distributionMethod,
                            patientHasSmartphone,
                        });

                        if (this.props.errors.size) {
                            await Promise.all([
                                this.props.validateOneCustomControl('typeSlaaprecorder'),
                                this.props.validateOneCustomControl('slaaprecorderVerzenden'),
                            ]);
                        }
                    }}
                    values={{
                        distributionMethod: this.props.request.customControlData.slaaprecorderVerzenden,
                        sleepRecorderType: this.props.request.customControlData.typeSlaaprecorder,
                        patientHasSmartphone: this.props.request.customControlData.patientHasSmartphone,
                    }}
                />

                <PatientContactAddEdit
                    showEmailOnly
                    {...this.props}
                />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!this.props.request.id}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        );
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                getSelectedLanguage().toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                getSelectedLanguage().toast.draftDefaultMessage
            );
        }
        await this.props.onSave(requestId);

        if (this.props.reloadProcess) {
            await this.props.reloadProcess(ReloadEpisode.reloadAll);
        }
        if (this.props.reloadPatient) {
            await this.props.reloadPatient();
        }
    }

    private async getOsasvServiceOrganizationSettings() {
        const url = `/api/serviceOrganizationSettings?serviceCode=OSASV&organizationId=${this.props.organizationId}&isActive=true`;
        const result = await fetchJson(url, 'GET');
        if (result.json.items.length === 1) {
            await this.setState({
                canReferPatientWithoutSmartphone: result.json.items[0].canReferPatientWithoutSmartphone,
            });
        }
    }

    private async getShouldCreateOsasq() {
        const shouldCreateOsasq = await osasExaminationCanCreateOsasq(this.props.request.patientId);
        await this.props.handleChangeCustomControl({ shouldCreateOsasq });
    }
}

export function osasvoV1RequestInitializeCustomControlData() {
    return {
        manuallyRequested: true,
        contactgegevens_patient: {
            emailadres: null,
        },
        typeSlaaprecorder: null,
        slaaprecorderVerzenden: null,
        shouldCreateOsasq: false,
        patientHasSmartphone: null,
    };
}
